import Purchases from "react-native-purchases";

import {
  type DiagnosticItem,
  DiagnosticStatus,
} from "@/schemas/app/diagnostic";

/**
 * Fetches detailed information about user's purchases and RevenueCat status
 * @returns Array of diagnostic items related to purchases
 */
export async function fetchPurchasesInfo(): Promise<DiagnosticItem[]> {
  const diagnosticItems: DiagnosticItem[] = [];

  try {
    // Get customer info
    const customerInfo = await Purchases.getCustomerInfo();

    const canUserPurchase = await Purchases.canMakePayments();
    diagnosticItems.push({
      id: "canPurchase",
      name: "Can Make Payments",
      value: canUserPurchase ? "Yes" : "No",
      status: canUserPurchase ? DiagnosticStatus.OK : DiagnosticStatus.ERROR,
    });

    // Get offerings
    const offerings = await Purchases.getOfferings();

    // Customer ID
    diagnosticItems.push({
      id: "customerId",
      name: "Customer ID",
      value: customerInfo.originalAppUserId || "Unknown",
      status:
        customerInfo.originalAppUserId !== null
          ? DiagnosticStatus.OK
          : DiagnosticStatus.ERROR,
    });

    // Active subscriptions
    const activeSubscriptions = customerInfo.activeSubscriptions || [];
    diagnosticItems.push({
      id: "subscriptions",
      name: "Active Subscriptions",
      value:
        activeSubscriptions.length > 0
          ? activeSubscriptions.join(", ")
          : "None",
      status: DiagnosticStatus.OK,
    });

    // Expiration dates for active entitlements
    const entitlements = customerInfo.entitlements.active || {};
    const entitlementInfo = Object.entries(entitlements).map(
      ([name, details]) =>
        `${name}: ${new Date(details.expirationDate || 0).toLocaleDateString() || "No expiration"}`,
    );

    diagnosticItems.push({
      id: "entitlements",
      name: "Active Entitlements",
      value: entitlementInfo.length > 0 ? entitlementInfo.join(", ") : "None",
      status: DiagnosticStatus.OK,
    });

    // Available offerings
    const availableOfferings = offerings?.all || {};
    const offeringInfo =
      Object.keys(availableOfferings).length > 0
        ? `${Object.keys(availableOfferings).length} offerings available`
        : "No offerings available";

    diagnosticItems.push({
      id: "offerings",
      name: "Available Offerings",
      value: offeringInfo,
      status:
        Object.keys(availableOfferings).length > 0
          ? DiagnosticStatus.OK
          : DiagnosticStatus.ERROR,
    });
  } catch (error) {
    // Handle any errors
    diagnosticItems.push({
      id: "purchases_error",
      name: "RevenueCat Error",
      value:
        error instanceof Error
          ? error.message
          : "Unknown error connecting to RevenueCat",
      status: DiagnosticStatus.ERROR,
    });
  }

  return diagnosticItems;
}

export default fetchPurchasesInfo;
